// "Ask @Kari to follow up" under an activity note's @-mention input
// (shared/_mention_follow_up.html.erb, server side Mentions::FollowUp).
//
// Once the note mentions at least one colleague, the control offers to turn
// the mention into a task: pick one of the mentioned colleagues and a due
// date, and saving the note also creates a task assigned to them.
//
// Candidates are the colleagues mentioned right now, in order of first
// appearance, minus the writer and minus anyone the "Will notify" facts
// (mention_audience.js) already know can't be asked: no longer on the team,
// or can't open the record. The server validates the same rules again.
//
// Mixed into mentionTextarea and tiptapEditor next to audienceState();
// audienceState#refreshAudience calls refreshFollowUp(tokens) on every
// change, and the partial calls initFollowUp($el) once it is in the DOM.
import { contextKey, knownMentionables, uniqueUserMentions } from "./mention_audience.js";

// [{ id, name }] colleagues who may be asked to follow up.
export function followUpCandidates(tokens, { known = new Map(), currentUserId = null } = {}) {
  const me = currentUserId ? String(currentUserId).toLowerCase() : null;
  const out = [];
  for (const { id, label } of uniqueUserMentions(tokens)) {
    if (id === me) continue;
    const fact = known.get(id);
    if (fact && (fact.missing || fact.can_view_context === false)) continue;
    out.push({ id, name: label || fact?.name || "" });
  }
  return out;
}

// The checkbox label: "Ask @Kari Berg to follow up" for a single candidate,
// the generic "Ask a mentioned colleague…" when the select picks among many.
// Templates carry a literal %{name}; the result is bound as text, never HTML.
export function followUpLabel(candidates, messages = {}) {
  if (candidates.length === 1 && messages.askOne) {
    // A replacer function, so "$&" in a name stays literal.
    return String(messages.askOne).replace("%{name}", () => candidates[0].name);
  }
  return String(messages.askAny || "");
}

export function followUpState() {
  return {
    followUpCandidates: [],
    followUpRequested: false,
    followUpAssigneeId: "",
    // Declared so Alpine never writes them to an outer x-data scope.
    _followUpTokens: [],
    _followUpMessages: {},
    _followUpSignature: "",
    _followUpEl: null,
    // A request handed over by adoptFollowUp, waiting for its colleague to be
    // among the candidates.
    _followUpPending: null,

    // Reads the localized strings and any state the server re-rendered (a
    // form that came back with errors keeps the box ticked and the choice).
    initFollowUp(el) {
      const data = el?.dataset || {};
      this._followUpEl = el || null;
      this._followUpMessages = { askOne: data.askOne || "", askAny: data.askAny || "" };
      this.followUpAssigneeId = String(data.assigneeId || "").toLowerCase();
      this.followUpRequested = data.requested === "true";
      this.refreshFollowUp(this._followUpTokens);
    },

    refreshFollowUp(tokens) {
      this._followUpTokens = tokens || [];
      const cfg = typeof this.audienceConfig === "function" ? this.audienceConfig() || {} : {};
      const candidates = followUpCandidates(this._followUpTokens, {
        known: knownMentionables(contextKey(cfg.contextType, cfg.contextId)),
        currentUserId: cfg.currentUserId
      });
      const signature = JSON.stringify(candidates.map((person) => [person.id, person.name]));
      if (signature !== this._followUpSignature) {
        this._followUpSignature = signature;
        this.followUpCandidates = candidates;
      }
      if (!candidates.length) {
        // Nobody left to ask: untick, so a later mention starts unticked.
        this.followUpRequested = false;
        return;
      }
      if (!candidates.some((person) => person.id === this.followUpAssigneeId)) {
        this.followUpAssigneeId = candidates[0].id;
      }
      this._applyPendingFollowUp();
    },

    // { requested, assigneeId, dueOn }: what this control would post, for a
    // sibling control to take over (the opportunity quick-log's plain and rich
    // editors each own one; log_activity_body.js hands it across on toggle).
    followUpSnapshot() {
      return {
        requested: !!this.followUpRequested,
        assigneeId: this.followUpAssigneeId,
        dueOn: this._followUpDueInput()?.value || ""
      };
    },

    // Takes over a sibling's followUpSnapshot(). The tick and choice apply once
    // the colleague is among this control's candidates (the note's text is
    // carried over separately and may land a moment later).
    adoptFollowUp(snapshot) {
      if (!snapshot) return;
      const due = this._followUpDueInput();
      if (due && snapshot.dueOn) due.value = snapshot.dueOn;
      if (!snapshot.requested) {
        this._followUpPending = null;
        this.followUpRequested = false;
        return;
      }
      this._followUpPending = { assigneeId: String(snapshot.assigneeId || "").toLowerCase() };
      this._applyPendingFollowUp();
    },

    _applyPendingFollowUp() {
      const pending = this._followUpPending;
      if (!pending) return;
      const person = this.followUpCandidates.find((candidate) => candidate.id === pending.assigneeId);
      if (!person) return;
      this._followUpPending = null;
      this.followUpAssigneeId = person.id;
      this.followUpRequested = true;
    },

    _followUpDueInput() {
      const el = this._followUpEl;
      return el && typeof el.querySelector === "function"
        ? el.querySelector('input[name="mention_follow_up[due_on]"]')
        : null;
    },

    followUpLabel() {
      return followUpLabel(this.followUpCandidates, this._followUpMessages);
    }
  };
}
